/**
 * Shared mini-UI helpers used across the B2B Hub tabs.
 */
import React, { useEffect, useState } from 'react';
import { HelpCircle, Loader2 } from 'lucide-react';

export const Centered: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="flex items-center justify-center py-16">{children}</div>
);

/** Small (i) icon - hover/focus shows what a control does. */
export const InfoTip: React.FC<{
  text: string;
  className?: string;
  align?: 'left' | 'center' | 'right';
}> = ({ text, className = '', align = 'center' }) => {
  const pos =
    align === 'left'
      ? 'left-0 translate-x-0'
      : align === 'right'
        ? 'right-0 translate-x-0'
        : 'left-1/2 -translate-x-1/2';

  return (
    <span
      className={`relative inline-flex shrink-0 group ${className}`}
      onClick={(e) => e.stopPropagation()}
    >
      <button
        type="button"
        tabIndex={0}
        className="p-0.5 rounded text-slate-400 hover:text-slate-600 focus:outline-none focus:ring-1 focus:ring-pink-300"
        aria-label="More info"
      >
        <HelpCircle className="w-3.5 h-3.5" />
      </button>
      <span
        role="tooltip"
        className={`pointer-events-none absolute z-50 bottom-full ${pos} mb-1.5 w-52 max-w-[16rem] px-2 py-1.5 text-[11px] leading-snug font-normal text-slate-700 bg-white border border-slate-200 rounded-md shadow-lg opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity`}
      >
        {text}
      </span>
    </span>
  );
};

/** Section heading with optional info tooltip (inline). */
export const HeadingWithInfo: React.FC<{
  children: React.ReactNode;
  info: string;
  className?: string;
}> = ({ children, info, className = '' }) => (
  <span className={`inline-flex items-center gap-1 ${className}`}>
    {children}
    <InfoTip text={info} />
  </span>
);

/** Button/control with trailing info icon. */
export const ActionWithInfo: React.FC<{ info: string; children: React.ReactNode; align?: 'left' | 'center' | 'right' }> = ({
  info,
  children,
  align = 'left',
}) => (
  <span className="inline-flex items-center gap-0.5">
    {children}
    <InfoTip text={info} align={align} />
  </span>
);

export const ErrorBox: React.FC<{ message: string; onRetry?: () => void }> = ({ message, onRetry }) => (
  <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">
    <div className="font-medium mb-1">Something went wrong</div>
    <div className="opacity-80">{message}</div>
    {onRetry && (
      <button onClick={onRetry} className="mt-2 text-red-700 underline hover:no-underline">
        Retry
      </button>
    )}
  </div>
);

/** Inline status while an async action runs - explains what's happening under the hood. */
export const LoadingNudge: React.FC<{
  title: string;
  step?: string;
  steps?: string[];
  className?: string;
}> = ({ title, step, steps, className = '' }) => (
  <div className={`flex items-start gap-2 px-3 py-2 text-xs bg-slate-50 border border-slate-200 rounded-md ${className}`}>
    <Loader2 className="w-4 h-4 animate-spin text-pink-600 shrink-0 mt-0.5" />
    <div className="min-w-0 flex-1">
      <div className="font-medium text-slate-800">{title}</div>
      {step && <div className="text-slate-600 mt-0.5">{step}</div>}
      {steps && steps.length > 0 && (
        <ul className="mt-1.5 space-y-0.5 text-[11px] text-slate-500">
          {steps.map((s, i) => (
            <li key={i} className={step === s ? 'text-pink-700 font-medium' : ''}>
              {step === s ? '→ ' : '· '}{s}
            </li>
          ))}
        </ul>
      )}
    </div>
  </div>
);

/**
 * Cycle through step labels while `active` - gives users a sense of progress
 * for long-running single-request operations (e.g. ATS detect ~5-30s).
 */
export function useStepProgress(active: boolean, steps: string[], intervalMs = 2800): string | null {
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    if (!active) {
      setIdx(0);
      return;
    }
    setIdx(0);
    const id = setInterval(() => {
      setIdx((i) => Math.min(i + 1, steps.length - 1));
    }, intervalMs);
    return () => clearInterval(id);
  }, [active, steps, intervalMs]);

  if (!active || steps.length === 0) return null;
  return steps[idx];
}

/** Debounce a string value (e.g. search box) to avoid hammering the API. */
export function useDebouncedValue<T>(value: T, delayMs = 350): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(id);
  }, [value, delayMs]);
  return debounced;
}
